import { Link } from '@tanstack/react-router';

import { IndexNumber, Tag } from '@/components/editorial';
import { AppImage } from '@/components/layout/app-image';
import type { PublicWritingRecord } from '@/content/schemas';
import type { WritingCategory } from '@/content/registry';

import { getCategoryLabel } from '../categories';

type WritingListCardProps = {
  post: PublicWritingRecord;
  index: number;
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatReadingMinutes(minutes: number) {
  return `${minutes} min read`;
}

export function WritingListCard({ post, index }: WritingListCardProps) {
  const categoryLabel = getCategoryLabel(post.category);

  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="border-border hover:border-brand/50 active:border-brand/50 group grid overflow-hidden rounded-2xl border transition-colors active:scale-[0.99] md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
    >
      <div className="bg-muted relative aspect-[16/10] md:aspect-auto md:min-h-[220px]">
        <AppImage
          src={post.hero.src}
          alt={post.hero.alt}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
      </div>

      <div className="flex flex-col gap-4 p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-widest">
            <span>{categoryLabel}</span>
            <span className="text-border">·</span>
            <span>{formatDate(post.publishedAt)}</span>
            <span className="text-border">·</span>
            <span>{formatReadingMinutes(post.readingMinutes)}</span>
          </div>
          <IndexNumber value={index} />
        </div>

        <div className="space-y-2">
          <h2 className="font-display text-ink group-hover:text-brand text-2xl font-semibold leading-snug transition-colors md:text-3xl">
            {post.title}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
            {post.description}
          </p>
        </div>

        <dl className="border-border grid grid-cols-3 gap-3 border-y py-4">
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
              Category
            </dt>
            <dd className="font-display text-ink text-sm font-semibold">
              {categoryLabel}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
              Read time
            </dt>
            <dd className="font-display text-ink text-sm font-semibold">
              {post.readingMinutes} min
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
              Tags
            </dt>
            <dd className="font-display text-ink text-sm font-semibold">
              {post.tags.length > 0 ? post.tags.length : '—'}
            </dd>
          </div>
        </dl>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1.5">
            {post.tags.slice(0, 4).map((tag) => (
              <Tag key={tag} variant="muted">
                {tag}
              </Tag>
            ))}
          </div>
          <span className="text-brand font-mono text-xs font-medium">
            Read post →
          </span>
        </div>
      </div>
    </Link>
  );
}

export function WritingCategoryNav({
  activeCategories,
  currentCategory,
}: {
  activeCategories: WritingCategory[];
  currentCategory?: WritingCategory;
}) {
  return (
    <div className="mb-10 flex flex-wrap items-center gap-2">
      <Tag variant={currentCategory ? 'muted' : 'active'} href="/blog">
        All
      </Tag>
      {activeCategories.map((category) => (
        <Tag
          key={category}
          variant={category === currentCategory ? 'active' : 'muted'}
          href={`/blog/category/${category}`}
        >
          {getCategoryLabel(category)}
        </Tag>
      ))}
    </div>
  );
}
